import type { ReferralLetterOptions } from "@rosc/contracts";
import { Link } from "@tanstack/react-router";
import { FileText, Settings2, Star } from "lucide-react";
import type { JSX } from "react";
import { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { WithDictation } from "@/components/ui/DictationButton";
import { V3Button } from "@/components/ui/v3/button";
import { V3ChipLabel } from "@/components/ui/v3/chip";
import {
  readReferralLastUsed,
  rememberReferralLastUsed,
} from "@/lib/referral-templates/last-used";
import {
  initialReferralChoice,
  type ReferralChoice,
  referralChoices,
  useReferralTemplates,
} from "@/lib/referral-templates/queries";
import {
  type ReferralSpecialtyKey,
  specialtyOptions,
  specialtyValue,
} from "@/lib/referral-templates/specialties";
import { cn } from "@/lib/utils";
import { useVisitScope } from "@/lib/visit-queries";
import { SpecialtyField } from "./SpecialtyField";
import { ReferralSheet } from "./sheet";
import {
  FIELD_HINT,
  FIELD_INPUT,
  FIELD_LABEL,
  FIELD_TEXTAREA,
  OPTION_CARD,
  RADIO,
} from "./styles";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGenerate: (options: ReferralLetterOptions) => void;
}

// Create → Referral letter (VITE_REFERRAL_TEMPLATES_ENABLED): who the letter
// goes to and how it is written, then the letter is generated from this
// visit's note and transcript. Starts on the template used last.
export const ReferralLetterDialog = ({
  open,
  onOpenChange,
  onGenerate,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const account = useVisitScope()?.accountPublicId;
  const templates = useReferralTemplates({ enabled: open });
  const choices = useMemo(
    () =>
      referralChoices(templates.builtIns, templates.items, (key) =>
        t(`referral.builtIn.${key}.name`),
      ),
    [templates.builtIns, templates.items, t],
  );
  const options = useMemo(
    () =>
      specialtyOptions((key: ReferralSpecialtyKey) =>
        t(`referral.specialties.${key}`),
      ),
    [t],
  );
  const [specialty, setSpecialty] = useState("");
  const [recipient, setRecipient] = useState("");
  const [reason, setReason] = useState("");
  const [templateId, setTemplateId] = useState<string | undefined>();
  const [touched, setTouched] = useState(false);
  const ids = {
    specialty: useId(),
    specialtyHint: useId(),
    recipient: useId(),
    reason: useId(),
    reasonHint: useId(),
    template: useId(),
  };

  // A fresh form each time it opens, on the last template and specialty.
  useEffect(() => {
    if (!open) return;
    const last = readReferralLastUsed(account);
    const label = last.specialty
      ? (options.find((option) => option.name === last.specialty)?.label ??
        last.specialty)
      : "";
    setSpecialty(label);
    setRecipient("");
    setReason("");
    setTouched(false);
    setTemplateId(undefined);
  }, [open, account, options]);
  useEffect(() => {
    if (!open || templateId || !choices.length) return;
    const first = choices.find(
      (choice) =>
        choice.id ===
        initialReferralChoice(
          choices,
          readReferralLastUsed(account).templateId,
        ),
    );
    if (!first) return;
    setTemplateId(first.id);
    // A template made for a specialty fills it in when nothing was remembered.
    if (first.specialty)
      setSpecialty(
        (current) =>
          current ||
          (options.find((option) => option.name === first.specialty)?.label ??
            first.specialty ??
            ""),
      );
  }, [open, templateId, choices, account, options]);

  // A template made for a specialty fills it in when nothing is typed yet.
  const pickTemplate = (choice: ReferralChoice): void => {
    setTemplateId(choice.id);
    if (!specialty.trim() && choice.specialty)
      setSpecialty(
        options.find((option) => option.name === choice.specialty)?.label ??
          choice.specialty,
      );
  };

  const specialtyMissing = !specialty.trim();
  const submit = (): void => {
    setTouched(true);
    if (specialtyMissing) {
      document.getElementById(ids.specialty)?.focus();
      return;
    }
    const value = specialtyValue(options, specialty);
    rememberReferralLastUsed(account, {
      ...(templateId ? { templateId } : {}),
      specialty: value,
    });
    onOpenChange(false);
    onGenerate({
      specialty: value,
      ...(recipient.trim() ? { recipient: recipient.trim() } : {}),
      ...(reason.trim() ? { reason: reason.trim() } : {}),
      ...(templateId ? { letterTemplate: templateId } : {}),
    });
  };

  return (
    <ReferralSheet
      description={t("referral.dialog.description")}
      footer={
        <>
          <V3Button
            className="max-sm:h-12"
            variant="secondary"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {t("common.cancel")}
          </V3Button>
          <V3Button
            className="max-sm:h-12"
            data-testid="referral-generate"
            type="submit"
            variant="primary"
          >
            {t("referral.dialog.generate")}
          </V3Button>
        </>
      }
      open={open}
      testId="referral-letter-dialog"
      title={t("referral.dialog.title")}
      onOpenChange={onOpenChange}
      onSubmit={submit}
    >
      <div className="space-y-1.5">
        <label className={FIELD_LABEL} htmlFor={ids.specialty}>
          {t("referral.dialog.specialty")}
        </label>
        <SpecialtyField
          describedBy={ids.specialtyHint}
          id={ids.specialty}
          invalid={touched && specialtyMissing}
          placeholder={t("referral.dialog.specialtyPlaceholder")}
          value={specialty}
          onChange={setSpecialty}
        />
        <p
          className={cn(
            FIELD_HINT,
            touched && specialtyMissing && "text-v3-rec-text",
          )}
          id={ids.specialtyHint}
        >
          {touched && specialtyMissing
            ? t("referral.dialog.specialtyRequired")
            : t("referral.dialog.specialtyHint")}
        </p>
      </div>

      <div className="space-y-1.5">
        <label className={FIELD_LABEL} htmlFor={ids.recipient}>
          {t("referral.dialog.recipient")}{" "}
          <span className="font-normal text-v3-text-3">
            {t("referral.dialog.optional")}
          </span>
        </label>
        <input
          autoComplete="off"
          className={FIELD_INPUT}
          data-testid="referral-recipient"
          id={ids.recipient}
          maxLength={200}
          placeholder={t("referral.dialog.recipientPlaceholder")}
          type="text"
          value={recipient}
          onChange={(event) => {
            setRecipient(event.target.value);
          }}
        />
      </div>

      <fieldset className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <legend className={FIELD_LABEL}>
            {t("referral.dialog.template")}
          </legend>
          <Link
            className="inline-flex min-h-11 items-center gap-1.5 rounded-full px-2 text-[13px] font-medium text-v3-text-2 hover:bg-v3-surface-2 hover:text-v3-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring sm:min-h-8"
            hash="referral-letters"
            to="/templates"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            <Settings2 aria-hidden className="size-4" />
            {t("referral.dialog.manageTemplates")}
          </Link>
        </div>
        {templates.error && (
          <p className={FIELD_HINT} role="status">
            {t("referral.dialog.templatesUnavailable")}
          </p>
        )}
        <div
          aria-busy={templates.loading || undefined}
          className="grid gap-2 sm:grid-cols-2"
        >
          {templates.loading && !choices.length
            ? [0, 1].map((index) => (
                <div
                  key={index}
                  aria-hidden
                  className="h-[60px] animate-pulse rounded-xl bg-v3-surface-2"
                />
              ))
            : choices.map((choice) => (
                <label key={choice.id} className={OPTION_CARD}>
                  <input
                    checked={templateId === choice.id}
                    className={RADIO}
                    data-testid={`referral-template-${choice.id}`}
                    name={ids.template}
                    type="radio"
                    value={choice.id}
                    onChange={() => {
                      pickTemplate(choice);
                    }}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5 text-[14px] font-semibold leading-5 text-v3-text">
                      <span className="truncate">{choice.name}</span>
                      {choice.isDefault && (
                        <Star
                          aria-label={t("referral.templates.default")}
                          className="size-3.5 shrink-0 fill-v3-text-2 text-v3-text-2"
                        />
                      )}
                    </span>
                    <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[12.5px] text-v3-text-2">
                      <span className="inline-flex items-center gap-1">
                        <FileText aria-hidden className="size-3.5" />
                        {choice.sections.length
                          ? t("referral.dialog.sectionCount", {
                              count: choice.sections.length,
                            })
                          : t("referral.dialog.paragraphs")}
                      </span>
                      {choice.builtIn ? (
                        <span>{t("referral.templates.builtIn")}</span>
                      ) : (
                        choice.specialty && (
                          <span className="truncate">
                            {options.find(
                              (option) => option.name === choice.specialty,
                            )?.label ?? choice.specialty}
                          </span>
                        )
                      )}
                    </span>
                  </span>
                </label>
              ))}
        </div>
      </fieldset>

      <div className="space-y-1.5">
        <label className={FIELD_LABEL} htmlFor={ids.reason}>
          {t("referral.dialog.reason")}{" "}
          <span className="font-normal text-v3-text-3">
            {t("referral.dialog.optional")}
          </span>
        </label>
        <WithDictation
          fieldLabel={t("dictation.field.fields.referralInstructions")}
          multiline
        >
          <textarea
            aria-describedby={ids.reasonHint}
            className={cn(FIELD_TEXTAREA, "min-h-[88px]")}
            data-testid="referral-reason"
            id={ids.reason}
            maxLength={2000}
            placeholder={t("referral.dialog.reasonPlaceholder")}
            rows={3}
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
            }}
          />
        </WithDictation>
        <p className={FIELD_HINT} id={ids.reasonHint}>
          {t("referral.dialog.reasonHint")}
        </p>
      </div>
    </ReferralSheet>
  );
};

// Inline label for "Default", "From example" etc. on template rows.
export const ReferralTag = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => <V3ChipLabel>{children}</V3ChipLabel>;
